/**
 * @file OPL 语言侧边栏组件
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Empty, Spin } from 'antd';
import { cy } from './DiagramCanvas';
import { OplEdgeRecord, OplItem, OplNodeRecord } from '../utils/opl-rules';
import { getEdgeColor } from './EdgeEditDialog';

interface OPLSidebarProps {
  visible: boolean;
  onClose: () => void;
  offsetRight?: number;
}

const ITEM_HEIGHT = 60;
const OVERSCAN_COUNT = 4;

const VirtualList: React.FC<{
  items: OplItem[];
  renderItem: (item: OplItem, index: number) => React.ReactNode;
  height: number;
  scrollRef?: React.MutableRefObject<HTMLDivElement | null>;
}> = ({ items, renderItem, height, scrollRef }) => {
  const [scrollTop, setScrollTop] = useState(0);
  const totalHeight = useMemo(() => items.length * ITEM_HEIGHT, [items.length]);

  const visibleRange = useMemo(() => {
    const startIndex = Math.max(
      0,
      Math.floor(scrollTop / ITEM_HEIGHT) - OVERSCAN_COUNT
    );
    const visibleCount = Math.ceil(height / ITEM_HEIGHT) + OVERSCAN_COUNT * 2;
    const endIndex = Math.min(items.length - 1, startIndex + visibleCount);
    return { startIndex, endIndex };
  }, [scrollTop, height, items.length]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  const visibleItems = useMemo(() => {
    const nodes: React.ReactNode[] = [];
    for (let i = visibleRange.startIndex; i <= visibleRange.endIndex; i++) {
      const item = items[i];
      if (!item) continue;
      const top = i * ITEM_HEIGHT;
      nodes.push(
        <div
          key={item.id}
          style={{
            position: 'absolute',
            top,
            left: 0,
            right: 0,
            height: ITEM_HEIGHT,
          }}
        >
          {renderItem(item, i)}
        </div>
      );
    }
    return nodes;
  }, [items, visibleRange, renderItem]);

  if (items.length === 0) return null;

  return (
    <div
      ref={scrollRef}
      style={{
        height,
        overflow: 'auto',
        position: 'relative',
      }}
      onScroll={handleScroll}
    >
      <div style={{ height: totalHeight, position: 'relative' }}>
        {visibleItems}
      </div>
    </div>
  );
};

const serializeGraphForWorker = () => {
  if (!cy)
    return { nodes: [] as OplNodeRecord[], edges: [] as OplEdgeRecord[] };

  const nodes: OplNodeRecord[] = [];
  cy.nodes().forEach((n: any) => {
    if (n.data('display') === 'none') return;
    if (n.data('isMarkerLabel')) return;
    const data = n.data();
    const parentId =
      data.parent || (n.isChild() && n.parent() ? n.parent().id() : '');
    nodes.push({
      id: n.id(),
      label: data.label || data.displayLabel || n.id(),
      type: data.type,
      parentId,
      childGroupType: data.childGroupType,
      essence: data.essence,
      affiliation: data.affiliation,
    });
  });

  const edges: OplEdgeRecord[] = [];
  cy.edges().forEach((e: any) => {
    if (e.data('display') === 'none') return;
    edges.push({
      id: e.id(),
      sourceId: e.data('source'),
      targetId: e.data('target'),
      type: e.data('type'),
      label: e.data('label'),
      linkQualifier: e.data('linkQualifier'),
      linkNegation: e.data('linkNegation'),
    });
  });

  return { nodes, edges };
};

const OPLSidebar: React.FC<OPLSidebarProps> = ({
  visible,
  onClose,
  offsetRight = 0,
}) => {
  const width = 260;
  const hiddenRight = -(width + offsetRight);
  const shownRight = offsetRight;
  const workerRef = useRef<Worker | null>(null);
  const debounceRef = useRef<number | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [items, setItems] = useState<OplItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [listHeight, setListHeight] = useState(420);
  const [workerReady, setWorkerReady] = useState(false);
  const [nodeMeta, setNodeMeta] = useState<
    Map<string, { label: string; color: string }>
  >(new Map());

  const ensureHeight = useCallback(() => {
    if (typeof window === 'undefined') return;
    const h = contentRef.current?.clientHeight;
    setListHeight(Math.max(260, h || window.innerHeight - 200));
  }, []);

  useEffect(() => {
    ensureHeight();
    window.addEventListener('resize', ensureHeight);
    return () => window.removeEventListener('resize', ensureHeight);
  }, [ensureHeight]);

  useEffect(() => {
    workerRef.current = new Worker(
      new URL('../workers/opl-worker.ts', import.meta.url),
      { type: 'module' }
    );
    const current = workerRef.current;
    current.onmessage = (event: MessageEvent<{ items: OplItem[] }>) => {
      setItems(event.data?.items || []);
      setLoading(false);
    };
    setWorkerReady(true);
    return () => {
      setWorkerReady(false);
      current.terminate();
    };
  }, []);

  const scheduleBuild = useCallback(() => {
    if (!visible || !workerRef.current || !cy) return;
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(() => {
      const payload = serializeGraphForWorker();
      setLoading(true);
      workerRef.current?.postMessage(payload);
    }, 400);
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    scheduleBuild();
    const rerender = () => scheduleBuild();
    if (cy) {
      cy.on('add remove data', rerender);
      cy.on('layoutstop', rerender);
    }
    return () => {
      if (cy) {
        cy.off('add remove data', rerender);
        cy.off('layoutstop', rerender);
      }
    };
  }, [visible, scheduleBuild]);

  useEffect(() => {
    if (!visible || !workerReady) return;
    scheduleBuild();
  }, [visible, workerReady, scheduleBuild]);

  useEffect(() => {
    if (!cy || !items.length) {
      setNodeMeta(new Map());
      return;
    }
    const ids = new Set<string>();
    items.forEach(item => item.nodeIds?.forEach(id => ids.add(id)));
    const map = new Map<string, { label: string; color: string }>();
    ids.forEach(id => {
      const n = cy.getElementById(id);
      if (!n || !n.length) return;
      const label = n.data('label') || n.data('displayLabel') || id;
      const color = getEdgeColor(n.data('type'));
      map.set(id, { label, color });
    });
    setNodeMeta(map);
  }, [items]);

  useEffect(() => {
    if (!visible || cy) return;
    const timer = window.setInterval(() => {
      if (cy) {
        scheduleBuild();
        window.clearInterval(timer);
      }
    }, 300);
    return () => window.clearInterval(timer);
  }, [visible, scheduleBuild]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleItemClick = useCallback((item: OplItem) => {
    if (!cy) return;
    cy.elements().unselect();
    let collection = cy.collection();
    if (item.nodeIds?.length) {
      item.nodeIds.forEach(id => {
        const ele = cy.getElementById(id);
        if (ele && ele.length) {
          collection = collection.union(ele);
        }
      });
    }
    if (item.edgeId) {
      const edge = cy.getElementById(item.edgeId);
      if (edge && edge.length) {
        collection = collection.union(edge);
      }
    }
    if (collection.length) {
      collection.select();
      cy.animate({
        center: { eles: collection },
        duration: 300,
      });
    }
  }, []);

  const renderItem = useCallback(
    (item: OplItem, index: number) => {
      const buildHighlight = () => {
        if (!item.nodeIds?.length || nodeMeta.size === 0) return item.text;
        const nodeInfos = item.nodeIds
          .map(id => {
            const info = nodeMeta.get(id);
            return info ? { ...info, id } : null;
          })
          .filter(Boolean) as { label: string; color: string; id: string }[];
        if (!nodeInfos.length) return item.text;

        const escapeReg = (s: string) =>
          s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

        const text = item.text || '';
        const hits: {
          start: number;
          end: number;
          color: string;
          label: string;
        }[] = [];
        nodeInfos.forEach(info => {
          const pattern = escapeReg(info.label).replace(/\\\s+/g, '\\s+');
          const regex = new RegExp(pattern, 'gi');
          let match: RegExpExecArray | null;
          while ((match = regex.exec(text)) !== null) {
            hits.push({
              start: match.index,
              end: match.index + match[0].length,
              color: info.color,
              label: text.slice(match.index, match.index + match[0].length),
            });
            if (match.index === regex.lastIndex) regex.lastIndex++;
          }
        });

        if (!hits.length) return text;

        // 按起点排序，起点相同则按长度降序（优先长匹配）
        hits.sort((a, b) =>
          a.start === b.start
            ? b.end - b.start - (a.end - a.start)
            : a.start - b.start
        );

        const merged: typeof hits = [];
        hits.forEach(hit => {
          const last = merged[merged.length - 1];
          if (!last || hit.start >= last.end) {
            merged.push(hit);
          }
        });

        const parts: React.ReactNode[] = [];
        let cursor = 0;
        merged.forEach(hit => {
          if (cursor < hit.start) parts.push(text.slice(cursor, hit.start));
          parts.push(
            <span
              key={`${item.id}-${hit.start}`}
              style={{ color: hit.color, fontWeight: 600 }}
            >
              {text.slice(hit.start, hit.end)}
            </span>
          );
          cursor = hit.end;
        });
        if (cursor < text.length) parts.push(text.slice(cursor));
        return parts;
      };

      return (
        <div
          key={item.id}
          className="opl-list-item !h-full"
          onClick={() => handleItemClick(item)}
        >
          <div className="opl-item-index">{index + 1}.</div>
          <div className="opl-item-text !text-left" title={item.text}>
            {buildHighlight()}
          </div>
        </div>
      );
    },
    [handleItemClick, nodeMeta]
  );

  return (
    <div
      className={`opl-sidebar ${visible ? 'visible' : ''}`}
      style={{ right: visible ? shownRight : hiddenRight, width }}
    >
      <div className="opl-sidebar-header">
        <h3>本体定义语言</h3>
        <button className="opl-sidebar-close" onClick={onClose} title="关闭">
          ×
        </button>
      </div>
      <div
        className="opl-sidebar-content  !px-0 !py-2"
        style={{ overflowY: 'hidden' }}
        ref={contentRef}
      >
        {loading && (
          <div className="opl-loading">
            <Spin size="small" /> <span className="ml-2">OPL 生成中...</span>
          </div>
        )}
        {visible && items.length > 0 && (
          <VirtualList
            items={items}
            renderItem={renderItem}
            height={listHeight}
            scrollRef={listRef}
          />
        )}
        {visible && !loading && items.length === 0 && (
          <div className="opl-empty">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="暂无 OPL 数据"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default OPLSidebar;
